import './weapons.css';
import { el, setText, toggle } from '../ui/dom';
import { weaponIcon } from '../ui/icons';
import { bracketSwitchPos, LOCK_SWITCH_EASE } from './lockFeel';
import type { WeaponDef } from './weaponData';

/**
 * Weapon-in-hand HUD: an instrument plate bottom-right (where the car panel sits when driving, so it hides
 * in a car) with a silhouette, the name, rounds loaded / spare and a reload bar; lock-on brackets
 * projected over the target; and the stamped ARMED POLICE warning. Self-contained DOM under .wpn-*
 * (the touch layout positions these for small screens). Updated once per frame, DOM writes only on change.
 */

/** Silhouettes (src/ui/icons.ts, shared with the touch HIT button), drawn in currentColor. */
function iconFor(d: WeaponDef): string {
  return weaponIcon(d.id, d.cls);
}

export interface WeaponHudFrame {
  def: WeaponDef;
  /** On foot and offline (the plate shows); false hides it. */
  visible: boolean;
  loaded: number;
  reserve: number;
  reload: number;
  /** How many weapons are carried (the "next" hint only shows with more than one). */
  carried: number;
  nextKey: string;
  /** Lock-on target on screen (px), or null. `key` names the target: the brackets ease over when it changes. */
  lock: { x: number; y: number; melee: boolean; key?: string } | null;
  /** An armed officer has the player covered. */
  warn: boolean;
  /** One of the player's blows or rounds just landed on somebody (screen px): the hit marker flashes there. */
  hit?: { x: number; y: number; kill: boolean } | null;
}

export class WeaponHud {
  readonly root: HTMLElement;
  private readonly icon: HTMLElement;
  private readonly name: HTMLElement;
  private readonly ammo: HTMLElement;
  private readonly loadedEl: HTMLElement;
  private readonly spareEl: HTMLElement;
  private readonly bar: HTMLElement;
  private readonly barFill: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly lock: HTMLElement;
  private readonly warn: HTMLElement;
  private readonly hitMark: HTMLElement;
  private shownId = '';
  private swapTimer = 0;
  private lockShown = false;
  private lockKey = '';
  private lockAt = { x: 0, y: 0 };
  private lockFrom = { x: 0, y: 0 };
  private lockEase = 0;

  constructor(parent: HTMLElement) {
    this.root = el('div', 'wpn-hud is-hidden', undefined, parent);
    this.icon = el('div', 'wpn-icon', undefined, this.root);
    this.name = el('div', 'wpn-name', '', this.root);
    this.ammo = el('div', 'wpn-ammo', undefined, this.root);
    this.loadedEl = el('b', '', '', this.ammo);
    this.spareEl = el('span', '', '', this.ammo);
    this.bar = el('div', 'wpn-reload', undefined, this.root);
    this.barFill = el('i', '', undefined, this.bar);
    this.hint = el('div', 'wpn-hint', '', this.root);
    this.lock = el('div', 'wpn-lock', undefined, parent);
    this.warn = el('div', 'wpn-warn', 'ARMED POLICE! STAND STILL!', parent);
    this.hitMark = el('div', 'wpn-hitmark', undefined, parent);
  }

  update(f: WeaponHudFrame, dt: number): void {
    const d = f.def;
    toggle(this.root, 'is-hidden', !f.visible);
    if (d.id !== this.shownId) {
      this.shownId = d.id;
      this.icon.innerHTML = iconFor(d);
      setText(this.name, d.name);
      toggle(this.root, 'is-gun', d.firearm !== null);
      this.root.classList.remove('is-swapped');
      void this.root.offsetWidth; // restart the swap animation
      this.root.classList.add('is-swapped');
      this.swapTimer = 0.4;
    }
    if (this.swapTimer > 0) {
      this.swapTimer -= dt;
      if (this.swapTimer <= 0) this.root.classList.remove('is-swapped');
    }
    if (d.firearm) {
      setText(this.loadedEl, String(f.loaded));
      setText(this.spareEl, `/ ${f.reserve}`);
      toggle(this.ammo, 'is-empty', f.loaded === 0);
    } else {
      setText(this.loadedEl, '');
      setText(this.spareEl, d.cls === 'unarmed' ? 'bare knuckles' : 'melee');
      toggle(this.ammo, 'is-empty', false);
    }
    toggle(this.bar, 'is-on', f.reload > 0);
    this.barFill.style.width = `${Math.round(f.reload * 100)}%`;
    setText(this.hint, f.carried > 1 ? `${f.nextKey}: next weapon` : '');
    if (f.lock && f.visible) {
      // A hand-over to another target eases the brackets across (lockFeel.ts); on one target they sit
      // on him with no lag; coming on from nothing they appear where he is.
      const key = f.lock.key ?? '';
      if (this.lockShown && key !== this.lockKey) {
        this.lockFrom = { ...this.lockAt };
        this.lockEase = 0;
      } else if (!this.lockShown) this.lockEase = LOCK_SWITCH_EASE;
      this.lockKey = key;
      this.lockShown = true;
      this.lockEase += dt;
      this.lockAt = bracketSwitchPos(this.lockFrom, f.lock, this.lockEase);
      toggle(this.lock, 'is-on', true);
      toggle(this.lock, 'is-melee', f.lock.melee);
      this.lock.style.left = `${this.lockAt.x.toFixed(0)}px`;
      this.lock.style.top = `${this.lockAt.y.toFixed(0)}px`;
    } else {
      this.lockShown = false;
      toggle(this.lock, 'is-on', false);
    }
    toggle(this.warn, 'is-on', f.warn);
    if (f.hit) {
      // A white cross where it landed (red for a kill), restarted on every hit; the brackets kick too.
      this.hitMark.style.left = `${f.hit.x.toFixed(0)}px`;
      this.hitMark.style.top = `${f.hit.y.toFixed(0)}px`;
      toggle(this.hitMark, 'is-kill', f.hit.kill);
      this.hitMark.classList.remove('is-on');
      this.lock.classList.remove('is-hit');
      void this.hitMark.offsetWidth;
      this.hitMark.classList.add('is-on');
      this.lock.classList.add('is-hit');
    }
  }

  dispose(): void {
    this.root.remove();
    this.lock.remove();
    this.warn.remove();
    this.hitMark.remove();
  }
}
